import React from 'react';
import { useOrgContext } from '@/contexts/OrgContext';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Bird, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router';
import { PageTopNav } from '@/components/nav/PageTopNav';

// Define the OrgSpecies type based on API response
interface OrgSpecies {
  id: string;
  speciesId: string;
  status: "ANNUAL" | "RESIDENT" | "UNCOMMON" | "VAGRANT" | "SCARCE" | "RARE";
  note: string;
  order: string;
  scientificName: string;
  englishName: string;
  recordCount: number;
}

// Define color mapping for status badges
const statusColorMap: Record<string, string> = {
  RESIDENT: 'bg-green-100 text-green-800 hover:bg-green-100',
  MIGRANT: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  VAGRANT: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
  SCARCE: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  RARE: 'bg-red-100 text-red-800 hover:bg-red-100',
};

const OrgSpecies: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { orgRole, organisation } = useOrgContext();
  // Fetch species from the organization
  const { data: orgSpecies, isLoading, error } = useQuery({
    queryKey: ['organizationSpecies'],
    queryFn: async () => {
      const response = await fetch(`/api/org/${orgId}/species`);
      if (!response.ok) {
        throw new Error('Failed to fetch organization species');
      }
      return response.json() as Promise<OrgSpecies[]>;
    },
  });

  return (
    <div className="container mx-auto">
      <PageTopNav
        pageName={`${organisation.name} Species`}
        rightItem={
          orgRole === "ADMIN" && (
            <Button
              onClick={() => navigate(`/app/org/${orgId}/addSpecies`)}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden md:inline">Add Species</span>
            </Button>
          )
        }
      />

      {
        isLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-800">
            Failed to load species. Please try again.
          </div>
        ) : (
          <>
            <p className="text-muted-foreground text-center mb-4">{organisation?.name} has recorded {orgSpecies?.length} species.</p>
            <div className="space-y-4">
              {orgSpecies && orgSpecies.length > 0 ? (
                <div className="grid gap-4">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b text-sm">
                        <th className="py-2 px-4 text-left">Species</th>
                        <th className="py-2 px-4 text-left">Status</th>
                        <th className="py-2 px-4 text-left">Records</th>
                        <th className="py-2 px-4 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgSpecies.map((orgSpecies) => (
                        <tr
                          key={orgSpecies.id}
                          className="border-b hover:bg-muted/50 cursor-pointer"
                          onClick={() => navigate(`/app/org/${orgId}/species/${orgSpecies.speciesId}`)}
                        >
                          <td className="py-2 px-4">
                            <div className='text-sm' >{orgSpecies.englishName}</div>
                            <div className="text-xs text-muted-foreground italic">{orgSpecies.scientificName}</div>
                          </td>
                          <td className="py-2 px-4">
                            <Badge
                              className={statusColorMap[orgSpecies.status] || 'bg-gray-100 text-gray-800'}
                            >
                              {orgSpecies.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-4">
                            {orgSpecies.recordCount}
                          </td>
                          <td className="py-2 px-4">
                            <div className="max-w-[300px] truncate" title={orgSpecies.note}>
                              {orgSpecies.note || '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center p-4 bg-muted/50 rounded-md">
                  <Bird className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">No species found in this organization's list.</p>
                </div>
              )}
            </div>
          </>
        )
      }
    </div >
  );
};

export default OrgSpecies;
